import React from "react";
import Decimal from "decimal.js-light";

// MUI
import {
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Helpers
import {
  printDate,
  isNaNOrNull,
  getTotalWeight,
  comboRegex,
} from "../helpers/methods";
import { replaceImageSource } from "../helpers/withReplaceImageSource";

// Hooks
import { useShippingMethod } from "./useShippingMethod";
import { getCreditPayment } from "./Credit/hooks";
import { getTaxCalculationWrapper } from "./Taxation/withTaxCalculation";
import { getGetDiscountValue } from "./Voucher/useGetDiscount";

// Components
import DeliveryOrderNumber from "./DeliveryOrderNumber";
import CustomBarcode from "./Common/CustomBarcode";
import { defaultInvoiceTable } from "./MyProfile/TableTemplate";
import { cellRenderers, DefaultCell } from "./InvoiceRenderer";

// Styles
import "./Order/BulkPrintDeliveryOrder/pagebreak.css";

const RootBox = styled(Box)(({ theme }) => ({
  display: "flex",
  "& > *": {
    margin: theme.spacing(1),
  },
}));

const LargeAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(10),
  height: theme.spacing(10),
}));

export const thickWidth = { width: "70%" };

const CompanyLogo = ({ ...props }) => (
  <RootBox>
    <LargeAvatar {...props} />
  </RootBox>
);

const ShippingOptionDeliveryOrderField = ({ shippingOption }) => {
  const [shippingOptions] = useShippingMethod({ showAll: true });
  const found = shippingOptions.find((opt) => opt.id === shippingOption);

  return <span>{found?.name ?? shippingOption}</span>;
};

class ComponentToPrint extends React.Component {
  render() {
    const {
      type = "invoice",
      record,
      companyName = "",
      companyRegistration = "",
      companyAddress = "",
      headerLine1 = "",
      headerLine2 = "",
      headerLine3 = "",
      logo = "",
      currency = "",
      table = {},
    } = this.props;
    const {
      orderItems = [],
      orderNumber,
      customerName,
      shippingAddress1 = "",
      shippingAddress2 = "",
      shippingCity = "",
      shippingName = "",
      shippingPhoneNumber = "",
      shippingPostcode = "",
      shippingState = "",
      shippingOption = "",
      shippingCost: shippingCostStr = "",
      description = "",
      notes = "",
      trackingNumber,
      createdAt,
      invoiceNumber,
      voucher = {},
      tax,
      status,
      payments = [],
    } = record;

    const {
      totalCreditUsed,
      taxAmount,
      totalAfterTax,
      merchandiseTotal,
      totalBeforeTax,
    } = getTaxCalculationWrapper(record);
    const { totalDiscount } = getGetDiscountValue(record);

    const tableMap = Object.fromEntries(table.map((f) => [f.key, f]));

    const shippingCost = isNaNOrNull(shippingCostStr)
      ? ""
      : new Decimal(shippingCostStr).toFixed(2);

    const beforeSortFiltered = orderItems.filter(
      (i) => i.status !== "cancelled"
    );

    let filtered = beforeSortFiltered;

    // First, check if user explicitly chose a sort
    switch (tableMap?.sortBy?.value) {
      case "dateAsc":
        filtered = beforeSortFiltered.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        break;

      case "dateDesc":
        filtered = beforeSortFiltered.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        break;

      case "descriptionAsc":
        filtered = beforeSortFiltered.sort((a, b) =>
          a.description
            ?.toLowerCase()
            .localeCompare(b.description?.toLowerCase())
        );
        break;

      case "keywordAsc":
        filtered = beforeSortFiltered.sort((a, b) => {
          const aKey = a.keyword?.key?.toLowerCase();
          const bKey = b.keyword?.key?.toLowerCase();

          if (aKey && !bKey) return -1;
          if (!aKey && bKey) return 1;
          if (!aKey && !bKey) return 0;

          return aKey.localeCompare(bKey);
        });
        break;

      case "detailAsc":
        filtered = beforeSortFiltered.sort((a, b) => {
          const aDetail = a.keyword?.detail?.toLowerCase();
          const bDetail = b.keyword?.detail?.toLowerCase();

          if (aDetail && !bDetail) return -1;
          if (!aDetail && bDetail) return 1;
          if (!aDetail && !bDetail) return 0;

          return aDetail.localeCompare(bDetail);
        });
        break;

      case "skuAsc":
        filtered = beforeSortFiltered.sort((a, b) => {
          const aSku = a.keyword?.sku?.toLowerCase();
          const bSku = b.keyword?.sku?.toLowerCase();

          if (aSku && !bSku) return -1; // a first
          if (!aSku && bSku) return 1; // b first
          if (!aSku && !bSku) return 0; // both missing, keep order

          return aSku.localeCompare(bSku); // normal compare
        });
        break;

      default:
        // Default = dateAsc
        filtered = beforeSortFiltered.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );

        // Then, only if the old flag is enabled, apply alphabetical on top
        if (tableMap?.sortItemAlphabetically?.enabled) {
          filtered = filtered.sort((a, b) =>
            a.description
              ?.toLowerCase()
              .localeCompare(b.description?.toLowerCase())
          );
        }
        break;
    }

    const totalItemWeight = getTotalWeight(filtered);

    const itemWithDescriptions = filtered.reduce((acc, cur) => {
      const {
        description = "",
        createdAt,
        price: priceStr = "",
        keyword = {},
        quantity: itemQuantity, // raw quantity from item
      } = cur;

      // Keep as Decimal for math
      const priceDecimal = isNaNOrNull(priceStr)
        ? new Decimal(0)
        : new Decimal(priceStr);

      const spacelessDescription = description.replace(/\s/g, " ").trim();
      const matches = comboRegex.exec(spacelessDescription);

      // Decide quantity (priority: regex → item → default 1)
      const regexQuantity = matches ? parseFloat(matches[2]) : null;
      const finalQuantity = regexQuantity || parseFloat(itemQuantity) || 1;

      // Calculate unit price safely
      const unitPrice =
        finalQuantity > 0 ? priceDecimal.div(finalQuantity) : new Decimal(0);

      // Base description (regex productDescription or whole desc)
      let baseDescription = matches ? matches[1] : spacelessDescription;

      const splitted = baseDescription.split(/,|，/).map((str, idx) => {
        if (idx === 0) {
          return {
            description: str.trim(), // pure description
            createdAt,
            price: priceDecimal.toFixed(2), // keep 2dp string for display
            quantity: finalQuantity,
            keyword,
            unitPrice: unitPrice.toFixed(2),
          };
        } else {
          return { description: `- ${str.trim()}`, createdAt };
        }
      });

      return [...acc, ...splitted];
    }, []);

    const totalQuantity = itemWithDescriptions.reduce((acc, cur) => {
      const quantity = cur.quantity;
      const qty = isNaNOrNull(parseInt(quantity)) ? 0 : parseInt(quantity);
      return acc + qty;
    }, 0);

    const title2 =
      type === "invoice" ? (tax ? "Tax Invoice" : "Invoice") : "Delivery Notes";
    const title3 = type === "invoice" ? "Invoice number" : "DO Number";
    const columns = table.filter((f) => f.enabled && f.inTable !== false);

    return (
      <Box>
        <Grid
          container
          spacing={1}
          columns={{ xs: 12 }}
          sx={{
            backgroundColor: "#fff",
            "& .MuiTableCell-root": {
              backgroundColor: "#fff",
              color: "#000",
            },
          }}
        >
          <Grid size={12}>
            <Box>
              <Grid
                container
                justifyContent="center"
                alignContent="center"
                alignItems="flex-start"
                columns={{ xs: 12 }}
              >
                <Grid size="auto">
                  <CompanyLogo
                    alt="Company logo"
                    src={replaceImageSource(logo)}
                  />
                </Grid>
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                >
                  <Grid>
                    <h1>{companyName}</h1>
                  </Grid>
                  <Grid>{companyRegistration}</Grid>
                  <Grid>
                    <h5>{companyAddress}</h5>
                  </Grid>
                  <Grid>{headerLine1}</Grid>
                  <Grid>{headerLine2}</Grid>
                  <Grid>{headerLine3}</Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid size={6}>
            <Box>
              <h4>Shipping address</h4>
              <div>{shippingName}</div>
              <div>{shippingPhoneNumber}</div>
              <div>{shippingAddress1}</div>
              <div>{shippingAddress2}</div>
              <div>{shippingCity}</div>
              <div>{shippingPostcode}</div>
              <div>{shippingState}</div>
              <br />
              <div>
                {`Shipping option: `}
                <ShippingOptionDeliveryOrderField
                  shippingOption={shippingOption}
                />
              </div>
              {shippingCost && <div>{`Shipping fee: ${shippingCost}`}</div>}
              {voucher?.code && <div>{`Voucher: ${voucher.code}`}</div>}
              {tableMap?.totalWeight?.enabled && (
                <div>{`Total Weight: ${totalItemWeight} kg`}</div>
              )}
              {trackingNumber && (
                <div>{`Tracking Number: ${trackingNumber}`}</div>
              )}
              {type !== "invoice" && <div>{`Order Status: ${status}`}</div>}
            </Box>
          </Grid>
          <Grid size={6}>
            <Box>
              <h4>{title2}</h4>
              <TableContainer component={Paper}>
                <Table aria-label="delivery order" size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell align="left">{title3}</TableCell>
                      <TableCell align="left">
                        <DeliveryOrderNumber invoiceNumber={invoiceNumber} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left">Order #</TableCell>
                      <TableCell align="left">{orderNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left">Nickname</TableCell>
                      <TableCell align="left">{customerName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left">Order date</TableCell>
                      <TableCell align="left">{printDate(createdAt)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left">Dispatch date</TableCell>
                      <TableCell align="left"></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left">Desc</TableCell>
                      <TableCell align="left">{description}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left">Order notes</TableCell>
                      <TableCell align="left">{notes}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
          <Grid size={12}>
            <Box>
              <TableContainer
                component={Paper}
                sx={{
                  overflowX: "hidden", // hide horizontal overflow
                  width: "100%",
                  // Optional: print-specific styling can go here as well
                  "@media print": {
                    overflowX: "visible",
                    boxShadow: "none", // remove shadow for print if you want
                  },
                }}
              >
                <Table border={1} aria-label="item table" size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          style={
                            col.key.includes("description") ? thickWidth : {}
                          }
                          padding={
                            col.key === "count" || col.key === "quantity"
                              ? "checkbox"
                              : "normal"
                          }
                          size={
                            col.key === "count" || col.key === "quantity"
                              ? "small"
                              : "medium"
                          }
                          align={
                            col.key.includes("description") ? "left" : "center"
                          }
                        >
                          {col?.tableLabel || col?.label}
                          {col.key === "quantity" && ` (${totalQuantity})`}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemWithDescriptions.map((row, idx) => (
                      <TableRow key={idx}>
                        {columns.map((col) => {
                          const Renderer =
                            cellRenderers[col.key] || DefaultCell;
                          return (
                            <React.Fragment key={col.key}>
                              {Renderer({ row, idx })}
                            </React.Fragment>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {tableMap.summary?.enabled && (
                <Box display="flex" justifyContent="flex-end" mt={1}>
                  <Paper
                    sx={{
                      p: 1.5,
                      width: 250,
                      backgroundColor: "#fff",
                      color: "#000",
                      colorScheme: "light",
                      "& .MuiTypography-root": {
                        color: "#000",
                      },
                    }}
                  >
                    {taxAmount > 0 ? (
                      <>
                        {/* Shipping */}
                        {shippingCost > 0 && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2">Shipping</Typography>
                            <Typography variant="body2">
                              {shippingCost}
                            </Typography>
                          </Box>
                        )}

                        {/* Discount */}
                        {voucher?.code && totalDiscount > 0 && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2">Discount</Typography>
                            <Typography variant="body2">
                              -{totalDiscount}
                            </Typography>
                          </Box>
                        )}

                        {/* Total Before Tax */}
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">
                            Total Before Tax
                          </Typography>
                          <Typography variant="body2">
                            {totalBeforeTax}
                          </Typography>
                        </Box>

                        {/* Tax */}
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">
                            {tax?.label || "Tax"}
                          </Typography>
                          <Typography variant="body2">{taxAmount}</Typography>
                        </Box>

                        {/* Credit */}
                        {totalCreditUsed > 0 && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2">
                              Credit Applied
                            </Typography>
                            <Typography variant="body2">
                              -{totalCreditUsed}
                            </Typography>
                          </Box>
                        )}

                        <Divider sx={{ my: 0.5 }} />

                        {/* Total After Tax */}
                        <Box display="flex" justifyContent="space-between">
                          <Typography fontWeight={600} variant="body2">
                            Total After Tax
                          </Typography>
                          <Typography fontWeight={600} variant="body2">
                            {`${currency} ${totalAfterTax}`}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <>
                        {/* Subtotal */}
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Subtotal</Typography>
                          <Typography variant="body2">
                            {merchandiseTotal}
                          </Typography>
                        </Box>

                        {/* Shipping */}
                        {shippingCost > 0 && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2">Shipping</Typography>
                            <Typography variant="body2">
                              {shippingCost}
                            </Typography>
                          </Box>
                        )}

                        {/* Discount */}
                        {voucher?.code && totalDiscount > 0 && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2">Discount</Typography>
                            <Typography variant="body2">
                              -{totalDiscount}
                            </Typography>
                          </Box>
                        )}

                        {/* Credit */}
                        {totalCreditUsed > 0 && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2">
                              Credit Applied
                            </Typography>
                            <Typography variant="body2">
                              -{totalCreditUsed}
                            </Typography>
                          </Box>
                        )}

                        <Divider sx={{ my: 0.5 }} />

                        {/* Total */}
                        <Box display="flex" justifyContent="space-between">
                          <Typography fontWeight={600} variant="body2">
                            Total
                          </Typography>
                          <Typography fontWeight={600} variant="body2">
                            {`${currency} ${totalAfterTax}`}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Paper>
                </Box>
              )}
            </Box>
          </Grid>
          {tableMap.footer?.enabled && (
            <Grid size={{ xs: 12 }} sx={{ textAlign: "center" }}>
              <Box p={2} />
              <h4>{tableMap.footer?.value}</h4>
            </Grid>
          )}

          {tableMap.footerOrderBarcode?.enabled && (
            <Grid size={{ xs: 12 }} sx={{ textAlign: "center" }}>
              <CustomBarcode value={orderNumber} height={30} width={2} />
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }
}

export default ComponentToPrint;
